import { NOTIFY_STATUS } from '@/utils/constants';
import { notificationMessage } from '@/utils/helpers';
import { deleteCookie, getCookie, setCookie } from '@/utils/helpers/storage';
import axios from 'axios';
import { AuthService } from './auth';

const APP_API_URL = process.env.REACT_APP_API_URL;

const axiosClient = axios.create({
  baseURL: APP_API_URL,
  headers: {
    'content-type': 'application/json',
  },
});

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  isRefreshing = false;
  failedQueue = [];
};

const refreshAccessToken = async () => {
  try {
    const refreshToken = getCookie('refreshToken');

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await AuthService.refreshToken({ refreshToken });

    const {
      AuthenticationResult: { AccessToken },
    } = response.data;

    // Update tokens in cookies
    setCookie('accessToken', AccessToken);
    // if (newRefreshToken) {
    //   setCookie('refreshToken', newRefreshToken);
    // }

    return AccessToken;
  } catch (error) {
    console.log('error', error);
    // Refresh token failed, redirect to login
    deleteCookie('accessToken');
    deleteCookie('refreshToken');
    window.location.href = '/login';
    throw error;
  }
};

axiosClient.interceptors.request.use(async (config: any) => {
  const customHeaders = {
    Authorization: '',
  };

  const token = getCookie('accessToken') ? getCookie('accessToken') : '';

  if (token) {
    customHeaders.Authorization = `Bearer ${token}`;
  }

  return {
    ...config,
    headers: {
      ...customHeaders, // auto attach token
      ...config.headers, // but you can override for some requests
    },
  };
});

axiosClient.interceptors.response.use(
  (response) => {
    return response?.data;
  },

  async (error) => {
    const {
      config,
      response: { status },
    } = error;

    if (config?.enableNotificationMessage) {
      notificationMessage({
        message: 'Bad Request',
        type: NOTIFY_STATUS.ERROR,
      });
    }

    // Handle 401 Unauthorized - attempt to refresh token
    if (status === 401) {
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const newAccessToken = await refreshAccessToken();

          // Update the failed request with new token
          config.headers.Authorization = `Bearer ${newAccessToken}`;

          processQueue(null, newAccessToken);

          // Retry the original request
          return axiosClient(config);
        } catch (err) {
          processQueue(err, null);
          return Promise.reject(err);
        }
      } else {
        // If already refreshing, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            config.headers.Authorization = `Bearer ${token}`;
            return axiosClient(config);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
    } else if (error.response.status === 403) {
      window.location.href = '/403';
    } else if (error.response.status === 429) {
      notificationMessage({
        message: 'Too Many Requests',
        type: NOTIFY_STATUS.ERROR,
      });
    } else {
      throw {
        ...error.response.data,
        status: error.response.status,
      };
    }
  },
);

export default axiosClient;
